import { cloneBoard, getPiece, opponent, promotionRow, rcToSquare, squareToRC, } from "./board.js";
const DIRS = [
    [-1, -1],
    [-1, 1],
    [1, -1],
    [1, 1],
];
/**
 * All single jumps available to `piece` on `from`, given `lifted` (the board
 * with the moving piece removed from its origin) and the squares already
 * captured in this sequence.
 *
 * FMJD 4.9: captured pieces are removed only at the end of the sequence, so
 * they remain on the board as blockers but may not be jumped twice ("Turkish
 * strike"). That is why `captured` is consulted rather than the pieces being
 * deleted as we go.
 */
function singleJumps(v, lifted, from, piece, captured) {
    const [r, c] = squareToRC(v, from);
    const enemy = opponent(piece.color);
    const steps = [];
    if (!piece.king) {
        // Men capture one square away. All four directions when the variant
        // allows backward capture; forward only otherwise.
        const dirs = v.menCaptureBackwards
            ? DIRS
            : DIRS.filter(([dr]) => dr === (piece.color === "w" ? -1 : 1));
        for (const [dr, dc] of dirs) {
            const midSq = rcToSquare(v, r + dr, c + dc);
            const landSq = rcToSquare(v, r + 2 * dr, c + 2 * dc);
            if (midSq === null || landSq === null)
                continue;
            const mid = getPiece(lifted, midSq);
            if (!mid || mid.color !== enemy || captured.has(midSq))
                continue;
            if (getPiece(lifted, landSq) !== null)
                continue; // landing must be empty
            steps.push({ captured: midSq, land: landSq });
        }
        return steps;
    }
    if (!v.flyingKings) {
        // Short king: capture like a man, but in all four directions.
        for (const [dr, dc] of DIRS) {
            const midSq = rcToSquare(v, r + dr, c + dc);
            const landSq = rcToSquare(v, r + 2 * dr, c + 2 * dc);
            if (midSq === null || landSq === null)
                continue;
            const mid = getPiece(lifted, midSq);
            if (!mid || mid.color !== enemy || captured.has(midSq))
                continue;
            if (getPiece(lifted, landSq) !== null)
                continue;
            steps.push({ captured: midSq, land: landSq });
        }
        return steps;
    }
    // Flying king: scan each diagonal, jump the first enemy piece, and land on
    // any empty square beyond it along the same diagonal (FMJD 4.6).
    for (const [dr, dc] of DIRS) {
        let rr = r + dr;
        let cc = c + dc;
        let victim = null;
        while (true) {
            const sq = rcToSquare(v, rr, cc);
            if (sq === null)
                break; // off board
            const occupant = getPiece(lifted, sq);
            if (victim === null) {
                if (occupant === null) {
                    rr += dr;
                    cc += dc;
                    continue; // empty square before the victim
                }
                // First non-empty square on this diagonal. An own piece blocks; so
                // does an already-captured enemy piece, which is still standing.
                if (occupant.color !== enemy || captured.has(sq))
                    break;
                victim = sq;
                rr += dr;
                cc += dc;
                continue;
            }
            // Past the victim: every empty square is a legal landing square.
            if (occupant !== null)
                break;
            steps.push({ captured: victim, land: sq });
            rr += dr;
            cc += dc;
        }
    }
    return steps;
}
/**
 * Depth-first expansion of a capture sequence. A sequence is only complete
 * when no further jump is available (FMJD 4.8: the capture must be played to
 * the end).
 */
function extendCaptures(v, lifted, from, piece, captured, path) {
    const capturedSet = new Set(captured);
    const jumps = singleJumps(v, lifted, from, piece, capturedSet);
    if (jumps.length === 0)
        return [];
    const results = [];
    for (const jump of jumps) {
        const nextCaptured = [...captured, jump.captured];
        const nextPath = [...path, jump.land];
        // FMJD 4.11: a man that passes over the promotion row mid-capture stays a
        // man and continues capturing as a man; it is promoted only if it ends
        // there. Variants where it promotes immediately (Russian) set
        // promoteMidCapture and continue the sequence as a king.
        let carrier = piece;
        if (!piece.king && v.promoteMidCapture) {
            const [landRow] = squareToRC(v, jump.land);
            if (landRow === promotionRow(v, piece.color)) {
                carrier = { color: piece.color, king: true };
            }
        }
        const deeper = extendCaptures(v, lifted, jump.land, carrier, nextCaptured, nextPath);
        if (deeper.length > 0) {
            results.push(...deeper);
        }
        else {
            results.push({ captures: nextCaptured, path: nextPath, end: jump.land });
        }
    }
    return results;
}
function isPromotion(v, piece, endSquare) {
    if (piece.king)
        return false;
    const [row] = squareToRC(v, endSquare);
    return row === promotionRow(v, piece.color);
}
/** All capture moves for `side`, filtered by the maximum-capture rule. */
export function generateCaptures(v, board, side) {
    const all = [];
    for (let sq = 1; sq <= v.numSquares; sq++) {
        const piece = getPiece(board, sq);
        if (!piece || piece.color !== side)
            continue;
        const lifted = cloneBoard(board);
        lifted[sq - 1] = null; // lift the moving piece: its origin is free again
        const sequences = extendCaptures(v, lifted, sq, piece, [], []);
        for (const seq of sequences) {
            all.push({
                from: sq,
                to: seq.end,
                captures: seq.captures,
                path: seq.path,
                promotion: isPromotion(v, piece, seq.end),
            });
        }
    }
    if (all.length === 0)
        return [];
    if (!v.maximumCapture)
        return all;
    // FMJD 4.13: the capture taking the greatest number of pieces is
    // compulsory. Quantity only — a king is worth no more than a man (4.14).
    let max = 0;
    for (const m of all)
        if (m.captures.length > max)
            max = m.captures.length;
    return all.filter((m) => m.captures.length === max);
}
/** All quiet (non-capturing) moves for `side`. */
export function generateQuietMoves(v, board, side) {
    const moves = [];
    for (let sq = 1; sq <= v.numSquares; sq++) {
        const piece = getPiece(board, sq);
        if (!piece || piece.color !== side)
            continue;
        const [r, c] = squareToRC(v, sq);
        if (!piece.king) {
            // Men always move forward only, in every FMJD variant.
            const forward = piece.color === "w" ? -1 : 1;
            for (const dc of [-1, 1]) {
                const dest = rcToSquare(v, r + forward, c + dc);
                if (dest === null || getPiece(board, dest) !== null)
                    continue;
                moves.push({
                    from: sq,
                    to: dest,
                    captures: [],
                    path: [dest],
                    promotion: isPromotion(v, piece, dest),
                });
            }
            continue;
        }
        if (!v.flyingKings) {
            for (const [dr, dc] of DIRS) {
                const dest = rcToSquare(v, r + dr, c + dc);
                if (dest === null || getPiece(board, dest) !== null)
                    continue;
                moves.push({ from: sq, to: dest, captures: [], path: [dest], promotion: false });
            }
            continue;
        }
        // Flying king slides any distance along an unobstructed diagonal.
        for (const [dr, dc] of DIRS) {
            let rr = r + dr;
            let cc = c + dc;
            while (true) {
                const dest = rcToSquare(v, rr, cc);
                if (dest === null || getPiece(board, dest) !== null)
                    break;
                moves.push({ from: sq, to: dest, captures: [], path: [dest], promotion: false });
                rr += dr;
                cc += dc;
            }
        }
    }
    return moves;
}
/** All legal moves for `side`. Captures are compulsory and take precedence. */
export function generateMoves(v, board, side) {
    const captures = generateCaptures(v, board, side);
    if (captures.length > 0)
        return captures;
    return generateQuietMoves(v, board, side);
}
/** Apply a move, returning a new board. Does not mutate the input. */
export function applyMove(board, move) {
    const next = cloneBoard(board);
    const piece = next[move.from - 1];
    if (!piece)
        throw new Error(`No piece on square ${move.from}`);
    next[move.from - 1] = null;
    for (const cap of move.captures)
        next[cap - 1] = null;
    next[move.to - 1] = { color: piece.color, king: piece.king || move.promotion };
    return next;
}
